import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/utils'

export type ToastVariant = 'success' | 'error' | 'info'

export type ToastInput = {
  message: string
  variant?: ToastVariant
  duration?: number
}

type ToastRecord = ToastInput & {
  id: string
}

type ToastContextValue = {
  pushToast: (input: ToastInput) => string
  dismissToast: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const variantStyles: Record<ToastVariant, string> = {
  success:
    'border border-emerald-500/30 bg-background text-foreground shadow-lg ring-1 ring-emerald-500/20',
  error:
    'border-2 border-destructive/80 bg-background text-foreground shadow-lg ring-1 ring-destructive/25',
  info: 'border border-border bg-background text-foreground shadow-lg ring-1 ring-border',
}

function ToastStackItem({
  toast,
  onDismiss,
}: {
  toast: ToastRecord
  onDismiss: (id: string) => void
}) {
  const variant = toast.variant ?? 'info'
  const duration = toast.duration ?? 4500

  useEffect(() => {
    if (duration <= 0) return
    const t = window.setTimeout(() => onDismiss(toast.id), duration)
    return () => window.clearTimeout(t)
  }, [toast.id, duration, onDismiss])

  return (
    <div
      role="status"
      className={cn(
        'w-full max-w-sm rounded-2xl px-4 py-3 motion-reduce:transition-none',
        variantStyles[variant],
      )}
    >
      <div className="flex items-start gap-3">
        <p className="flex-1 text-sm font-medium leading-snug">{toast.message}</p>
        <button
          type="button"
          onClick={() => onDismiss(toast.id)}
          className="shrink-0 rounded-lg px-2 py-0.5 text-xs font-semibold text-muted-foreground transition hover:bg-muted hover:text-foreground"
        >
          Dismiss
        </button>
      </div>
    </div>
  )
}

function ToastStackPortal({
  toasts,
  onDismiss,
}: {
  toasts: ToastRecord[]
  onDismiss: (id: string) => void
}) {
  if (toasts.length === 0) return null
  return createPortal(
    <div
      className="pointer-events-none fixed top-20 right-4 z-[300] flex w-[min(100vw-2rem,24rem)] flex-col gap-2 sm:right-6"
      aria-live="polite"
      aria-relevant="additions"
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastStackItem toast={toast} onDismiss={onDismiss} />
        </div>
      ))}
    </div>,
    document.body,
  )
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([])

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const pushToast = useCallback((input: ToastInput) => {
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    setToasts((prev) => [...prev.slice(-4), { ...input, id }])
    return id
  }, [])

  const value = useMemo(() => ({ pushToast, dismissToast }), [pushToast, dismissToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastStackPortal toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
